/**
 * Livacils Product Quiz
 * 5-question scoring → product recommendation + Klaviyo capture
 */

(function () {
  'use strict';

  const quiz = document.querySelector('[data-quiz]');
  if (!quiz) return;

  const TOTAL_STEPS = 5;
  const answers = {};

  // Product scoring weights
  const PRODUCT_WEIGHTS = {
    rehaussement: { natural_discreet: 3, natural_marked: 2, glam_soft: 1, drama: 0 },
    magnetiques: { natural_discreet: 0, natural_marked: 2, glam_soft: 3, drama: 1 },
    extensions: { natural_discreet: 0, natural_marked: 1, glam_soft: 2, drama: 3 },
    bundle: { natural_discreet: 1, natural_marked: 2, glam_soft: 2, drama: 2 },
  };

  const RESULT_PROFILES = {
    rehaussement: {
      title: 'Le Regard Naturel Sublimé',
      desc: 'Tu es pour le naturel : tes cils amplifiés sans que ça se voie. Le Kit Rehaussement est fait pour toi : ultra-confort, zéro colle, résultat bluffant.',
      cta: 'Je veux ce kit',
      handle: 'kit-rehaussement',
    },
    magnetiques: {
      title: 'Le Regard Magnétique',
      desc: 'Tu veux du style sans te prendre la tête. Les Cils Magnétiques s\'appliquent en 30 secondes et transforment ton regard en un clin d\'œil.',
      cta: 'Découvrir les magnétiques',
      handle: 'cils-magnetiques',
    },
    extensions: {
      title: 'Le Regard Intense',
      desc: 'Tu assumes, tu assumes fort. Les Extensions Volume sont ta signature : dramatiques, longues, parfaites pour sortir sous les projecteurs.',
      cta: 'Je veux les extensions',
      handle: 'extensions-volume',
    },
    bundle: {
      title: 'La Routine Complète',
      desc: 'Tu veux tout : la praticité du quotidien ET le wow effect pour les grandes occasions. Notre bundle sur-mesure te couvre pour toutes les humeurs.',
      cta: 'Voir mon bundle',
      handle: 'bundle-routine',
    },
  };

  // ─── Navigation ──────────────────────────────────────────────────
  let currentStep = 1;

  const progressBar = quiz.querySelector('[data-quiz-progress]');
  const stepEls = quiz.querySelectorAll('[data-quiz-step]');
  const resultEl = quiz.querySelector('[data-quiz-result]');

  function showStep(step) {
    stepEls.forEach((el) => {
      el.setAttribute('data-active', String(el.dataset.quizStep === String(step)));
    });

    if (progressBar) {
      progressBar.style.width = `${((step - 1) / TOTAL_STEPS) * 100}%`;
    }

    // Update counter
    const counter = quiz.querySelector('[data-step-counter]');
    if (counter) {
      counter.textContent = `Question ${step} sur ${TOTAL_STEPS}`;
    }
  }

  function nextStep() {
    if (currentStep < TOTAL_STEPS) {
      currentStep++;
      showStep(currentStep);
    } else {
      showResult();
    }
  }

  // ─── Option selection ────────────────────────────────────────────
  quiz.querySelectorAll('[data-quiz-option]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const step = btn.closest('[data-quiz-step]');
      if (!step) return;

      const stepNum = step.dataset.quizStep;
      const value = btn.dataset.quizOption;

      // Visual selection
      step.querySelectorAll('[data-quiz-option]').forEach((b) => b.removeAttribute('data-selected'));
      btn.setAttribute('data-selected', 'true');

      // Save answer
      answers[stepNum] = value;

      // Auto-advance after short delay
      setTimeout(nextStep, 400);
    });
  });

  // ─── Scoring ─────────────────────────────────────────────────────
  function calculateResult() {
    const scores = { rehaussement: 0, magnetiques: 0, extensions: 0, bundle: 0 };

    Object.values(answers).forEach((value) => {
      Object.keys(scores).forEach((product) => {
        scores[product] += PRODUCT_WEIGHTS[product]?.[value] || 0;
      });
    });

    return Object.entries(scores).sort(([, a], [, b]) => b - a)[0][0];
  }

  // ─── Result ──────────────────────────────────────────────────────
  function showResult() {
    const winner = calculateResult();
    const profile = RESULT_PROFILES[winner];

    if (!resultEl || !profile) return;

    // Update result content
    const titleEl = resultEl.querySelector('[data-result-title]');
    const descEl = resultEl.querySelector('[data-result-desc]');
    const ctaEl = resultEl.querySelector('[data-result-cta]');

    if (titleEl) titleEl.textContent = profile.title;
    if (descEl) descEl.textContent = profile.desc;
    if (ctaEl) {
      ctaEl.textContent = profile.cta;
      ctaEl.href = `/products/${profile.handle}`;
    }

    // Show result, hide steps
    stepEls.forEach((el) => el.setAttribute('data-active', 'false'));
    resultEl.setAttribute('data-active', 'true');

    if (progressBar) progressBar.style.width = '100%';

    // Save to localStorage
    try {
      localStorage.setItem(
        'livacils_quiz_result',
        JSON.stringify({ winner, profile: profile.title, timestamp: Date.now(), answers })
      );
    } catch {}

    // Track with Klaviyo
    if (window.klaviyo) {
      window.klaviyo.push(['track', 'Completed Quiz', {
        QuizResult: winner,
        ResultTitle: profile.title,
        RecommendedProduct: profile.handle,
        Answers: answers,
      }]);
    }
  }

  // ─── Email capture ────────────────────────────────────────────────
  const emailForm = quiz.querySelector('[data-quiz-email-form]');
  if (emailForm) {
    emailForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const email = emailForm.querySelector('input[type="email"]')?.value;
      if (!email) return;

      const btn = emailForm.querySelector('button[type="submit"]');
      if (btn) { btn.disabled = true; btn.textContent = 'Envoi...'; }

      const quizResult = JSON.parse(localStorage.getItem('livacils_quiz_result') || '{}');

      // Klaviyo identify + subscribe
      if (window.klaviyo) {
        window.klaviyo.push(['identify', { email }]);
        window.klaviyo.push(['track', 'Quiz Email Capture', {
          Email: email,
          QuizResult: quizResult.winner,
          RecommendedProduct: quizResult.profile,
        }]);
      }

      // Show success
      emailForm.innerHTML = `
        <div style="text-align:center; padding: 20px 0;">
          <p style="font-family:var(--font-serif);font-size:var(--text-h4);color:var(--color-bronze);margin-bottom:8px">✓ C'est noté !</p>
          <p style="font-size:var(--text-small);color:var(--color-gray-600)">Vérifie ta boîte mail : ton code -10 % arrive.</p>
        </div>
      `;
    });
  }

  // ─── Restart ─────────────────────────────────────────────────────
  quiz.querySelector('[data-quiz-restart]')?.addEventListener('click', () => {
    Object.keys(answers).forEach((k) => delete answers[k]);
    currentStep = 1;
    stepEls.forEach((el) => el.removeAttribute('data-selected'));
    resultEl?.removeAttribute('data-active');
    showStep(1);
  });

  // Init
  showStep(1);

})();
